/**
 * iOS/iPadOS Safari routes Web Audio through the "ambient" audio session by
 * default, which is silenced by the hardware mute / silent switch — so a muted
 * iPhone or iPad produces *zero* game audio even though the AudioContext reports
 * `state === "running"` and `source.start()` ran. Declaring the session as
 * "playback" tells WebKit this is deliberate media playback that should sound
 * through the silent switch, which is the correct behaviour for a rhythm game.
 *
 * The AudioSession spec is still an Editor's Draft and only WebKit ships it, so
 * we feature-detect — on every other browser this is a no-op. WebKit guidance
 * is to set the type before resuming/playing and from within the user gesture,
 * so callers invoke this right as they unlock their AudioContext.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/AudioSession/type
 */

type AudioSessionType =
  | 'auto'
  | 'playback'
  | 'transient'
  | 'transient-solo'
  | 'ambient'
  | 'play-and-record';

// Named distinctly from the spec's eventual global `AudioSession` so this local
// shim can't collide once `lib.dom.d.ts` ships the real (wider) interface.
interface WebKitAudioSession {
  type: AudioSessionType;
}

declare global {
  interface Navigator {
    readonly audioSession?: WebKitAudioSession;
  }
}

let loggedConfiguration = false;

/**
 * Mark the document's audio session as "playback" so Web Audio is audible even
 * when the iOS/iPadOS silent switch is on. Idempotent and safe to call on every
 * AudioContext unlock; a no-op where the AudioSession API is unavailable.
 */
export function enablePlaybackAudioSession(): void {
  const session = navigator.audioSession;
  if (!session) {
    return;
  }
  try {
    session.type = 'playback';
    if (!loggedConfiguration) {
      console.info('[audio] navigator.audioSession.type set to "playback" (iOS silent-switch bypass)');
      loggedConfiguration = true;
    }
  } catch (err) {
    console.warn('[audio] Failed to set navigator.audioSession.type = "playback":', err);
  }
}
